import { handleNetworkError, safeOperation } from './errorHandler';
const API_CONFIG = {
  timeout: 30000, // 30 seconds (increased from 10s)
  retries: 3,
  retryDelay: 1000, // 1 second
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
};

// Base URL for PHP API
const getBaseURL = () => {
  if (process.env.NODE_ENV === 'production') {
    // Production domain - PHP API (clean URLs!)
    return 'https://meet.jethro-inc.id/api';
  }
  return 'http://localhost:3000/api';
};

// Test API availability
const testAPIConnection = async (baseUrl) => {
  try {
    const response = await fetch(`${baseUrl}/auth/login`, {
      method: 'OPTIONS',
      headers: { 'Accept': 'application/json' }
    });
    return response.ok || response.status < 500;
  } catch (error) {
    console.warn('API connection test failed:', error.message);
    return false;
  }
};

let BASE_URL = getBaseURL();

// Fallback untuk development jika production API tidak tersedia
if (process.env.NODE_ENV === 'production') {
  testAPIConnection(BASE_URL).then(isAvailable => {
    if (!isAvailable) {
      console.warn('Production API not available, check backend deployment');
    }
  });
}

const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeout || API_CONFIG.timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...API_CONFIG.headers,
        ...options.headers
      }
    });
    
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw handleNetworkError('Request timeout', url);
    }
    
    throw handleNetworkError(error.message, url, { originalError: error });
  }
};

// HTTP status code helpers
export const isSuccessStatus = (status) => status >= 200 && status < 300;
export const isClientError = (status) => status >= 400 && status < 500;
export const isServerError = (status) => status >= 500 && status < 600;

// Response parser
const parseResponse = async (response, url) => {
  const contentType = response.headers.get('content-type');
  
  try {
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      return await response.text();
    }
  } catch (error) {
    throw handleNetworkError('Invalid response format', url, { 
      status: response.status,
      contentType 
    });
  }
};

// Base API request function with authentication
const apiRequest = async (endpoint, options = {}) => {
  return safeOperation(async () => {
    const url = `${BASE_URL}${endpoint}`;
    
    // Add authentication token if available (unless skipAuth is true)
    if (!options.skipAuth) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        options.headers = {
          ...options.headers,
          'Authorization': `Bearer ${token}`
        };
      }
    }
    
    // Remove skipAuth from options before fetch
    const { skipAuth, ...fetchOptions } = options;
    
    const response = await fetchWithTimeout(url, fetchOptions);
    
    if (!isSuccessStatus(response.status)) {
      const errorData = await parseResponse(response, url);
      
      console.error('API Error:', url, response.status, errorData);
      
      // Handle authentication errors
      if (response.status === 401) {
        // DON'T auto-redirect for public endpoints like /digital-cards
        const publicEndpoints = ['/digital-cards', '/health', '/test'];
        const isPublicEndpoint = publicEndpoints.some(path => endpoint.startsWith(path));
        
        if (!isPublicEndpoint) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          localStorage.removeItem('digital_id_session');
          window.location.href = '/login';
          return;
        } else {
          console.log('ℹ️ Public endpoint, continuing without auth...');
          // For public endpoints, return the error data instead of redirecting
          return errorData;
        }
      }
      
      throw handleNetworkError(
        `HTTP ${response.status}: ${response.statusText}`,
        url,
        { 
          status: response.status,
          statusText: response.statusText,
          data: errorData
        }
      );
    }
    
    return parseResponse(response, url);
  });
};

export const apiGet = async (endpoint, skipAuth = false, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'GET',
    skipAuth: skipAuth
  });
};

// POST request
export const apiPost = async (endpoint, data = null, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : null
  });
};

// PUT request
export const apiPut = async (endpoint, data = null, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : null
  });
};

export const apiDelete = async (endpoint, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'DELETE'
  });
};

// PATCH request
export const apiPatch = async (endpoint, data = null, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : null
  });
};

// Upload file (FormData)
export const apiUpload = async (endpoint, formData, options = {}) => {
  const uploadOptions = {
    ...options,
    method: 'POST',
    body: formData,
    headers: {
      // Don't set Content-Type for FormData, let browser set it
      ...options.headers
    }
  };
  
  // Remove Content-Type header for FormData uploads
  if (uploadOptions.headers['Content-Type']) {
    delete uploadOptions.headers['Content-Type'];
  }
  
  return apiRequest(endpoint, uploadOptions);
};

// Batch requests
export const apiBatch = async (requests) => {
  return Promise.allSettled(
    requests.map(({ url, method = 'GET', data = null, options = {} }) => {
      switch (method.toUpperCase()) {
        case 'GET':
          return apiGet(url, options);
        case 'POST':
          return apiPost(url, data, options);
        case 'PUT':
          return apiPut(url, data, options);
        case 'DELETE':
          return apiDelete(url, options);
        case 'PATCH':
          return apiPatch(url, data, options);
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
    })
  );
};

// Request interceptor
let requestInterceptor = null;
export const setRequestInterceptor = (interceptor) => {
  requestInterceptor = interceptor;
};

// Response interceptor
let responseInterceptor = null;
export const setResponseInterceptor = (interceptor) => {
  responseInterceptor = interceptor;
};

// Enhanced API request with interceptors
const apiRequestWithInterceptors = async (url, options = {}) => {
  let requestOptions = options;
  
  // Apply request interceptor
  if (requestInterceptor) {
    requestOptions = await requestInterceptor(url, options);
  }
  
  let response = await apiRequest(url, requestOptions);
  
  // Apply response interceptor
  if (responseInterceptor) {
    response = await responseInterceptor(response, url, requestOptions);
  }
  
  return response;
};

// API with authentication
export const createAuthenticatedAPI = (getAuthToken) => {
  const authInterceptor = (url, options) => {
    const token = getAuthToken();
    if (token) {
      return {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`
        }
      };
    }
    return options;
  };
  
  return {
    get: (url, options = {}) => apiRequestWithInterceptors(url, {
      ...authInterceptor(url, options),
      method: 'GET'
    }),
    post: (url, data = null, options = {}) => apiRequestWithInterceptors(url, {
      ...authInterceptor(url, options),
      method: 'POST',
      body: data ? JSON.stringify(data) : null
    }),
    put: (url, data = null, options = {}) => apiRequestWithInterceptors(url, {
      ...authInterceptor(url, options),
      method: 'PUT',
      body: data ? JSON.stringify(data) : null
    }),
    delete: (url, options = {}) => apiRequestWithInterceptors(url, {
      ...authInterceptor(url, options),
      method: 'DELETE'
    }),
    patch: (url, data = null, options = {}) => apiRequestWithInterceptors(url, {
      ...authInterceptor(url, options),
      method: 'PATCH',
      body: data ? JSON.stringify(data) : null
    })
  };
};

// Network status checker
export const checkNetworkStatus = async () => {
  if (!navigator.onLine) {
    return { online: false, message: 'No internet connection' };
  }
  
  try {
    await fetch('/favicon.ico', { 
      method: 'HEAD',
      cache: 'no-cache'
    });
    return { online: true, message: 'Connected' };
  } catch (error) {
    return { online: false, message: 'Connection issues detected' };
  }
};

// Request cache (simple in-memory cache)
const requestCache = new Map();

export const getCachedRequest = (key) => {
  const cached = requestCache.get(key);
  if (cached && cached.expiry > Date.now()) {
    return cached.data;
  }
  return null;
};

export const setCachedRequest = (key, data, ttl = 300000) => { // 5 minutes default
  requestCache.set(key, {
    data,
    expiry: Date.now() + ttl
  });
};

export const clearCache = () => {
  requestCache.clear();
};

// Cached API request
export const apiGetCached = async (url, options = {}, ttl = 300000) => {
  const cacheKey = `${url}_${JSON.stringify(options)}`;
  const cached = getCachedRequest(cacheKey);
  
  if (cached) {
    return cached;
  }
  
  const response = await apiGet(url, options);
  setCachedRequest(cacheKey, response, ttl);
  
  return response;
};

// API health check
export const apiHealthCheck = async (endpoints = []) => {
  const results = await Promise.allSettled(
    endpoints.map(async (endpoint) => {
      const start = Date.now();
      try {
        await apiGet(endpoint);
        return {
          endpoint,
          status: 'healthy',
          responseTime: Date.now() - start
        };
      } catch (error) {
        return {
          endpoint,
          status: 'unhealthy',
          error: error.message,
          responseTime: Date.now() - start
        };
      }
    })
  );
  
  return results.map(result => result.value);
};

export const getAPIConfig = () => ({ ...API_CONFIG });
export const updateAPIConfig = (newConfig) => {
  Object.assign(API_CONFIG, newConfig);
};

// Digital Card API - Fetch by slug or ID
export const getDigitalCard = async (slugOrId) => {
  try {
    console.log('🔍 getDigitalCard called with:', slugOrId);
    const response = await apiGet(`/digital-cards/${slugOrId}`, true); // skipAuth for public access
    console.log('📥 getDigitalCard raw response:', response);
    if (response && response.data !== undefined) {
      console.log('✅ Returning response.data:', response.data);
      return response.data;
    }
    console.log('⚠️ No response.data, returning whole response');
    return response;
  } catch (error) {
    console.error('❌ Error fetching digital card:', error);
    return null;
  }
};
